import React, { type CSSProperties } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { Snippet } from '../snippetTypes.ts';
import { wrapAdfWithDocNode } from '../snippetUtils.ts';

import { getDataProviders } from './dataProviders.ts';

const styles = cssMap({
	scaledContainer: {
		overflow: 'hidden',
		userSelect: 'none',
		pointerEvents: 'none',
	},
	scaledContent: {
		height: 'auto',
		transformOrigin: 'top left',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
});

/** Desired output dimensions (container size) */
export type Dimensions = Pick<CSSProperties, 'width' | 'maxHeight'>;

export const SnippetPreview = ({
	snippet,
	dimensions,
	/** Width in px at which content renders before scaling */
	renderWidth,
}: {
	snippet: Snippet;
	dimensions: Dimensions;
	renderWidth: number;
}) => {
	return (
		<ScaledContainer dimensions={dimensions} renderWidth={renderWidth}>
			<Renderer adf={snippet.body} />
		</ScaledContainer>
	);
};

const Renderer = ({ adf }: { adf: any }) => {
	const { cloudId, userId } = useSessionData();

	const handleRenderError = (error: any) => {
		const reasonString = error instanceof Error ? error.message : JSON.stringify(error);
		const errorToLog = new Error(`Error when rendering snippet preview: ${reasonString}`);
		getMonitoringClient().submitError(errorToLog, {
			attribution: Attribution.INTEGRATIONS,
		});
	};

	const dataProviders = getDataProviders(cloudId, userId);

	return (
		<ADFRenderer
			document={wrapAdfWithDocNode(adf)}
			appearance="full-page"
			allowPlaceholderText
			dataProviders={dataProviders}
			onError={handleRenderError}
		/>
	);
};

const ScaledContainer = ({
	children,
	dimensions,
	renderWidth,
}: {
	children: React.ReactNode;
	dimensions: Dimensions;
	renderWidth: number;
}) => {
	return (
		<Box
			ref={(node: HTMLDivElement | null) => node?.setAttribute('inert', '')}
			xcss={styles.scaledContainer}
			style={{ width: dimensions.width, maxHeight: dimensions.maxHeight }}
		>
			<Box
				xcss={styles.scaledContent}
				style={{
					transform: `scale(calc((${dimensions.width}) / ${renderWidth}px))`,
					width: renderWidth,
				}}
			>
				{children}
			</Box>
		</Box>
	);
};
